"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { CalendarCheck, Globe, Menu, Workflow, X } from "lucide-react";
import { formatJobTitleLabel } from "@/lib/job-title";
import { SUPPORTED_LOCALES, t, useI18n } from "@/lib/i18n";
import LiquidLogoReveal from "@/components/LiquidLogoReveal";
import { useStoredRbacAccess } from "@/lib/use-rbac-access";

type AppNavbarProps = {
  accountName: string;
  initials: string;
  isAccountOpen: boolean;
  isMobileSidebarOpen: boolean;
  jobTitle: string | null;
  moduleDotClass: string;
  showSidebarToggle: boolean;
  theme: "light" | "dark";
  onAccountToggle: () => void;
  onLogoClick: () => void;
  onSidebarToggle: () => void;
};

export default function AppNavbar({
  accountName,
  initials,
  isAccountOpen,
  isMobileSidebarOpen,
  jobTitle,
  moduleDotClass,
  showSidebarToggle,
  theme,
  onAccountToggle,
  onLogoClick,
  onSidebarToggle,
}: AppNavbarProps) {
  const { locale, setLocale } = useI18n();
  const pathname = usePathname();
  const isTasksPath = pathname?.startsWith("/tasks") ?? false;
  const isFlowAibasPath = pathname?.startsWith("/tasks/flow-aibas") ?? false;
  const isWeeklyCorsecPath = pathname?.startsWith("/tasks/weekly-corsec") ?? false;
  const { roles } = useStoredRbacAccess();
  const isSuperAdmin = roles.includes("super_admin");

  return (
    <nav
      className={`fixed top-0 z-50 w-full border-b transition-colors duration-500 ${
        theme === "dark"
          ? "border-[color:var(--color-border-dark)] bg-[color:var(--color-surface-dark)]"
          : "border-[color:var(--color-border)] bg-[color:var(--color-surface)]/95 backdrop-blur-md"
      }`}
    >
      <div className="flex h-14 w-full items-center justify-between px-3 sm:h-16 sm:px-4 lg:px-6">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          {showSidebarToggle && (
            <button
              type="button"
              aria-label={isMobileSidebarOpen ? t("Close navigation menu") : t("Open navigation menu")}
              aria-controls="mobile-app-sidebar"
              aria-expanded={isMobileSidebarOpen}
              onClick={onSidebarToggle}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[color:var(--color-border)] text-[color:var(--color-text-primary)] transition-colors hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 xl:hidden"
            >
              {isMobileSidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onLogoClick}
              aria-label={t("Go to the first accessible module")}
              className={`flex h-10 w-24 items-center justify-center overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 sm:h-12 sm:w-32 ${
                theme === "dark" ? "bg-transparent" : "bg-black"
              }`}
            >
              {/* The artwork has transparent margins, so the canvas is wider than the button. */}
              <span className="w-[7.5rem] shrink-0 sm:w-[10rem]">
                <LiquidLogoReveal src="/aibas-logo.png" alt={t("AiBAS")} playOnView={false} replayOnClick={false} />
              </span>
            </button>

            <span
              aria-hidden="true"
              className={`mb-3 ml-1 h-2 w-2 rounded-full transition-colors duration-500 ${moduleDotClass}`}
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {isTasksPath && (
            <Link
              href="/tasks/flow-aibas"
              aria-label="Flow-Aibas"
              aria-current={isFlowAibasPath ? "page" : undefined}
              title={t("Overall CRM and OPS process flow")}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11px] font-black tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                isFlowAibasPath
                  ? "border-transparent bg-[color:var(--color-primary)] text-white shadow-sm"
                  : theme === "dark"
                    ? "border-[color:var(--color-border-dark)] text-[color:var(--color-text-secondary)] hover:bg-white/10"
                    : "border-[color:var(--color-border)] bg-white/70 text-[color:var(--color-text-secondary)] hover:bg-slate-100"
              }`}
            >
              <Workflow size={14} aria-hidden="true" />
              <span className="hidden sm:inline">Flow-Aibas</span>
            </Link>
          )}
          {isTasksPath && isSuperAdmin && (
            <Link
              href="/tasks/weekly-corsec"
              aria-label="Weekly Corsec"
              aria-current={isWeeklyCorsecPath ? "page" : undefined}
              title={t("Weekly application development update")}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11px] font-black tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                isWeeklyCorsecPath
                  ? "border-transparent bg-[color:var(--color-primary)] text-white shadow-sm"
                  : theme === "dark"
                    ? "border-[color:var(--color-border-dark)] text-[color:var(--color-text-secondary)] hover:bg-white/10"
                    : "border-[color:var(--color-border)] bg-white/70 text-[color:var(--color-text-secondary)] hover:bg-slate-100"
              }`}
            >
              <CalendarCheck size={14} aria-hidden="true" />
              <span className="hidden sm:inline">Weekly Corsec</span>
            </Link>
          )}
          <div
            role="group"
            aria-label={t("Language")}
            className={`flex items-center gap-1 rounded-full border p-0.5 ${
              theme === "dark"
                ? "border-[color:var(--color-border-dark)]"
                : "border-[color:var(--color-border)] bg-white/70"
            }`}
          >
            <Globe size={14} aria-hidden="true" className="ml-1.5 text-[color:var(--color-text-secondary)]" />
            {SUPPORTED_LOCALES.map((option) => (
              <button
                key={option.code}
                type="button"
                onClick={() => setLocale(option.code)}
                aria-pressed={locale === option.code}
                title={t(option.label)}
                className={`rounded-full px-2 py-1 text-[11px] font-black tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                  locale === option.code
                    ? "bg-[color:var(--color-primary)] text-white shadow-sm"
                    : "text-[color:var(--color-text-secondary)] hover:bg-slate-100"
                }`}
              >
                {option.short}
              </button>
            ))}
          </div>
        <button
          type="button"
          onClick={onAccountToggle}
          aria-label={t("Open account settings")}
          aria-expanded={isAccountOpen}
          className="group flex min-w-0 max-w-[180px] cursor-pointer items-center gap-2 border-l border-[color:var(--color-border)] pl-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 sm:max-w-[220px] sm:pl-3 dark:border-[color:var(--color-border-dark)]"
        >
          <span className="hidden min-w-0 text-right md:block">
            <span
              className={`block truncate text-sm font-bold leading-none transition-colors ${
                theme === "dark" ? "text-white" : "text-[color:var(--color-text-primary)]"
              }`}
            >
              {accountName || t("Account User")}
            </span>
            <span className="mt-1 block truncate text-xs text-[color:var(--color-text-secondary)]">
              {formatJobTitleLabel(jobTitle)}
            </span>
          </span>

          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-tr from-[color:var(--color-primary)] to-[color:var(--color-text-secondary)] font-bold text-white shadow-lg ring-2 ring-white/20 transition-all group-hover:ring-offset-2 sm:h-10 sm:w-10">
            {initials}
          </span>
        </button>
        </div>
      </div>
    </nav>
  );
}
